import { useEffect, useRef, useState } from 'react';

/**
 * Keeps an element mounted long enough to animate out.
 *
 * React removes a component the moment its condition goes false, which means an
 * exit animation never runs: the node is gone before the first frame. This holds
 * the node for the duration of the transition and reports which phase it is in,
 * so the CSS has something to hang off.
 *
 * Deliberately not a library. One hook and two class names cover every modal,
 * drawer and menu in the panel, and an animation runtime is a large dependency
 * for a control panel whose animations are all "fade and move 4 pixels".
 */

export type TransitionPhase = 'entering' | 'entered' | 'exiting';

export function useMountTransition(
  open: boolean,
  durationMs = 180,
): { mounted: boolean; phase: TransitionPhase } {
  const [mounted, setMounted] = useState(open);
  const [phase, setPhase] = useState<TransitionPhase>(open ? 'entered' : 'exiting');
  const frame = useRef<number>(0);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setPhase('entering');

      // Two frames: one for the browser to paint the entering state, one to
      // flip to the entered state so the transition has something to run
      // between. A single frame lands in the same paint and nothing moves.
      frame.current = requestAnimationFrame(() => {
        frame.current = requestAnimationFrame(() => setPhase('entered'));
      });

      return () => cancelAnimationFrame(frame.current);
    }

    if (!mounted) return;

    setPhase('exiting');
    const timer = setTimeout(() => setMounted(false), durationMs);
    return () => clearTimeout(timer);
  }, [open, durationMs, mounted]);

  return { mounted, phase };
}
